import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { MotionPanelReveal } from "@themes/pollinator/ui/MotionPanelReveal";

export type Props = {
  /** Content for the left panel (dark) */
  leftPanel: ReactNode;
  /** Content for the right panel (light) */
  rightPanel: ReactNode;
  /** Additional className for the root element */
  className?: string;
  /** Additional className for the left panel */
  leftClassName?: string;
  /** Additional className for the right panel */
  rightClassName?: string;
  /** Test ID for the root element */
  testId?: string;
};

/**
 * Animated two-panel layout with 60/40 split.
 *
 * Left panel (dark, 60%): Animates from 100% to 60% width
 * Right panel (light, 40%): Animates from 0 to 40% width with fade
 *
 * On mobile, panels stack vertically without animation.
 */
export function TwoPanelLayout({
  leftPanel,
  rightPanel,
  className,
  leftClassName,
  rightClassName,
  testId = "two-panel-layout",
}: Props) {
  return (
    <div
      className={twMerge(
        "flex flex-col md:flex-row md:w-full min-h-screen",
        className
      )}
      data-testid={testId}
    >
      {/* Left Panel - Desktop (with animation) */}
      <MotionPanelReveal
        as="section"
        initialWidth="100%"
        animateWidth="60%"
        initialOpacity={1}
        animateOpacity={1}
        className={twMerge(
          "bg-brand-800 flex-col hidden md:flex",
          leftClassName
        )}
        testId={`${testId}-left`}
      >
        {leftPanel}
      </MotionPanelReveal>

      {/* Left Panel - Mobile (no animation) */}
      <section
        className={twMerge(
          "w-full bg-brand-50 flex flex-col md:hidden",
          leftClassName
        )}
        data-testid={`${testId}-left-mobile`}
      >
        {leftPanel}
      </section>

      {/* Right Panel - Desktop (with animation) */}
      <MotionPanelReveal
        as="section"
        initialWidth={0}
        animateWidth="40%"
        initialOpacity={0}
        animateOpacity={1}
        className={twMerge(
          "bg-brand-50 flex-col min-h-screen hidden md:flex",
          rightClassName
        )}
        testId={`${testId}-right`}
      >
        {rightPanel}
      </MotionPanelReveal>

      {/* Right Panel - Mobile (no animation) */}
      <section
        className={twMerge(
          "flex-1 bg-brand-50 flex flex-col min-h-[50vh] md:hidden",
          rightClassName
        )}
        data-testid={`${testId}-right-mobile`}
      >
        {rightPanel}
      </section>
    </div>
  );
}

export default TwoPanelLayout;
